import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import CabinaTopbar from '@/components/cabina/CabinaTopbar'
import MapZones from '@/components/cabina/MapZones'
import MapExpandOverlay from '@/components/cabina/MapExpandOverlay'
import {
  getAccountQuota,
  getInbox,
  listMyJobs,
  type AccountQuota,
  type InboxItem,
  type UserJobSummary,
} from '@/services/listingsService'
import { api } from '@/lib/api'
import type { SubscriptionPlan } from '@/types'
import {
  computeHomeAggregates,
  computeTrend,
  daysUntil,
  type HomeAggregates,
  type TrendAgg,
  type Opportunity,
} from '@/lib/homeAggregates'
import {
  jobStatusMeta,
  jobZoneLabel,
  jobCategoryLabel,
  jobLeads,
  formatDateTimeShort,
} from '@/pages/searchShared'
import type { OppBand } from '@/lib/opportunityScore'
import {
  getDemoInbox,
  DEMO_JOBS,
  DEMO_QUOTA,
  DEMO_PLAN,
  DEMO_TOTAL_LEADS,
} from '@/pages/dashboardDemo'

/* ---------------------------------------------------------------------------
 * Formato
 * ------------------------------------------------------------------------- */
const nInt = (n: number) => n.toLocaleString('es-ES')
const nDec1 = (n: number) =>
  n.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

const PLAN_ES: Record<string, string> = { free: 'Gratuito', starter: 'Starter', pro: 'Pro' }

const isPreview =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('preview')

/* ---------------------------------------------------------------------------
 * Iconos (inline, estilo del mockup)
 * ------------------------------------------------------------------------- */
type IconName =
  | 'plus' | 'search' | 'clock' | 'map' | 'check-circle' | 'mail' | 'globe'
  | 'phone' | 'star' | 'bars' | 'trend' | 'history' | 'bolt' | 'brain'
  | 'wa' | 'arrow' | 'lock' | 'screen' | 'users' | 'home'

function Icon({ name }: { name: IconName }) {
  const c = { fill: 'none', stroke: 'currentColor', strokeWidth: 2 } as const
  switch (name) {
    case 'plus': return <svg viewBox="0 0 24 24" {...c}><path d="M12 5v14M5 12h14" /></svg>
    case 'search': return <svg viewBox="0 0 24 24" {...c}><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>
    case 'clock': return <svg viewBox="0 0 24 24" {...c}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
    case 'map': return <svg viewBox="0 0 24 24" {...c}><path d="M9 20l-5 2V6l5-2 6 2 5-2v16l-5 2-6-2z" /><path d="M9 4v16M15 6v16" /></svg>
    case 'check-circle': return <svg viewBox="0 0 24 24" {...c}><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg>
    case 'mail': return <svg viewBox="0 0 24 24" {...c}><path d="M3 8l9 6 9-6" /><rect x="3" y="5" width="18" height="14" rx="2" /></svg>
    case 'globe': return <svg viewBox="0 0 24 24" {...c}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 010 18M12 3a15 15 0 000 18" /></svg>
    case 'phone': return <svg viewBox="0 0 24 24" {...c}><path d="M5 4h4l2 5-3 2a12 12 0 006 6l2-3 5 2v4a2 2 0 01-2 2A18 18 0 013 6a2 2 0 012-2z" /></svg>
    case 'star': return <svg viewBox="0 0 24 24" {...c}><path d="M12 2l2.4 7.4H22l-6 4.5 2.3 7.1L12 16.8 5.7 21l2.3-7.1-6-4.5h7.6z" /></svg>
    case 'bars': return <svg viewBox="0 0 24 24" {...c}><path d="M3 3v18h18" /><rect x="7" y="11" width="3" height="7" /><rect x="12" y="7" width="3" height="11" /><rect x="17" y="13" width="3" height="5" /></svg>
    case 'trend': return <svg viewBox="0 0 24 24" {...c}><path d="M3 17l6-6 4 4 8-8" /><path d="M21 7v5h-5" /></svg>
    case 'history': return <svg viewBox="0 0 24 24" {...c}><path d="M3 12a9 9 0 109-9 9 9 0 00-9 9z" /><path d="M3 4v5h5" /><path d="M12 7v5l3 2" /></svg>
    case 'bolt': return <svg viewBox="0 0 24 24" {...c}><path d="M13 2L3 14h7l-1 8 10-12h-7z" /></svg>
    case 'brain': return <svg viewBox="0 0 24 24" {...c}><path d="M12 2a7 7 0 00-4 12.7V17h8v-2.3A7 7 0 0012 2z" /><path d="M9 21h6M10 17v4M14 17v4" /></svg>
    case 'wa': return <svg viewBox="0 0 24 24" {...c}><path d="M21 11.5a8.4 8.4 0 01-12.3 7.4L3 21l2.1-5.7A8.4 8.4 0 1121 11.5z" /></svg>
    case 'arrow': return <svg viewBox="0 0 24 24" {...c}><path d="M5 12h14M13 6l6 6-6 6" /></svg>
    case 'lock': return <svg viewBox="0 0 24 24" {...c}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>
    case 'screen': return <svg viewBox="0 0 24 24" {...c}><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M8 20h8M12 18v2" /></svg>
    case 'users': return <svg viewBox="0 0 24 24" {...c}><path d="M16 3.1a4 4 0 010 7.8M8 21v-7M8 14a4 4 0 100-8 4 4 0 000 8z" /><path d="M16 21v-3a4 4 0 00-3-3.9" /></svg>
    case 'home': return <svg viewBox="0 0 24 24" {...c}><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3" /></svg>
  }
}

/* ---------------------------------------------------------------------------
 * Sub-componentes
 * ------------------------------------------------------------------------- */
function Donut({ consumed, quota }: { consumed: number; quota: number }) {
  const r = 50
  const c = 2 * Math.PI * r
  const ratio = quota > 0 ? Math.min(1, consumed / quota) : 0
  const off = c * (1 - ratio)
  return (
    <div className="donut">
      <svg width="116" height="116" viewBox="0 0 116 116">
        <circle cx="58" cy="58" r={r} fill="none" stroke="var(--panel-2)" strokeWidth="12" />
        <circle
          cx="58" cy="58" r={r} fill="none" stroke="var(--accent-2)" strokeWidth="12"
          strokeLinecap="round" strokeDasharray={c.toFixed(2)} strokeDashoffset={off.toFixed(2)}
        />
      </svg>
      <div className="center">
        <div>
          <div className="big">{nInt(consumed)}</div>
          <div className="small">de {nInt(quota)}</div>
        </div>
      </div>
    </div>
  )
}

const SEM_GLYPH: Record<OppBand, string> = { high: '✓', mid: '!', low: '✕' }
function Sem({ band }: { band: OppBand }) {
  return <span className={`sem ${band}`}>{SEM_GLYPH[band]}</span>
}

function OppActions({ o }: { o: Opportunity }) {
  const slots: { name: IconName; primary?: boolean; title: string }[] = []
  if (o.contact.whatsapp) slots.push({ name: 'wa', primary: true, title: 'WhatsApp' })
  if (o.contact.email) slots.push({ name: 'mail', title: 'Email' })
  if (o.contact.phone && !o.contact.whatsapp) slots.push({ name: 'phone', title: 'Llamar' })
  slots.push({ name: 'plus', title: 'Añadir al pipeline' })
  return (
    <div className="oc-act">
      {slots.slice(0, 3).map((s, i) => (
        <button key={i} type="button" className={`qa${s.primary ? ' primary' : ''}`} title={s.title}>
          <Icon name={s.name} />
        </button>
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * Carga de datos
 * ------------------------------------------------------------------------- */
interface HomeState {
  loading: boolean
  error: string | null
  quota: AccountQuota | null
  planName: string
  agg: HomeAggregates | null
  trend: TrendAgg | null
  recentJobs: UserJobSummary[]
  items: InboxItem[]
}

/** Top N jobs por fecha de creación (más recientes primero) para el widget
 *  "Continúa donde lo dejaste". */
function topRecentJobs(jobs: UserJobSummary[], limit = 4): UserJobSummary[] {
  return [...jobs]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, limit)
}

function demoState(): HomeState {
  const demo = getDemoInbox()
  return {
    loading: false,
    error: null,
    quota: DEMO_QUOTA,
    planName: DEMO_PLAN.name_es,
    agg: computeHomeAggregates(demo, DEMO_TOTAL_LEADS),
    trend: computeTrend(DEMO_JOBS),
    recentJobs: topRecentJobs(DEMO_JOBS),
    items: demo,
  }
}

function useHomeData(): HomeState {
  const [state, setState] = useState<HomeState>({
    loading: true, error: null, quota: null, planName: '', agg: null, trend: null, recentJobs: [], items: [],
  })

  useEffect(() => {
    let cancelled = false
    const ctrl = new AbortController()

    async function load() {
      try {
        const [quota, inbox, jobs] = await Promise.all([
          getAccountQuota(ctrl.signal),
          // El backend acepta hasta 500 en /inbox; los agregados se computan
          // sobre esta muestra (suficiente para %/distribuciones; el headline
          // de total usa el `total` real del endpoint).
          getInbox({}, { limit: 500, signal: ctrl.signal }),
          listMyJobs({ limit: 50, signal: ctrl.signal }),
        ])
        let planName = PLAN_ES[quota.plan_code] ?? quota.plan_code
        try {
          const pr = await api<{ plans: SubscriptionPlan[] }>('/billing/plans', { signal: ctrl.signal })
          const p = pr.plans?.find((x) => x.code === quota.plan_code)
          if (p) planName = p.name_es
        } catch {
          /* el nombre del plan ya tiene fallback */
        }
        if (cancelled) return
        setState({
          loading: false, error: null, quota, planName,
          agg: computeHomeAggregates(inbox.items, inbox.total),
          trend: computeTrend(jobs.items),
          recentJobs: topRecentJobs(jobs.items),
          items: inbox.items,
        })
      } catch (e) {
        if (cancelled || (e as { code?: string })?.code === 'aborted') return
        if (isPreview) {
          setState(demoState())
          return
        }
        setState((s) => ({ ...s, loading: false, error: 'No se pudieron cargar tus datos. Reintenta.' }))
      }
    }
    load()
    return () => { cancelled = true; ctrl.abort() }
  }, [])

  return state
}

/* ---------------------------------------------------------------------------
 * Página
 * ------------------------------------------------------------------------- */
export default function Dashboard() {
  const { loading, error, quota, planName, agg, trend, recentJobs, items } = useHomeData()

  return (
    <div className="cabina-root">
      <CabinaTopbar />
      <div className="wrap">
        <div className="page-head">
          <div>
            <div className="crumb"><Icon name="home" />Centro de inteligencia</div>
            <h1>Buenos días, Nico</h1>
            <p>Tu cabina de oportunidad: dónde tienes leads, qué calidad tienen y a quién conviene contactar primero.</p>
          </div>
          <Link to="/app/busqueda" className="btn btn-accent"><Icon name="plus" />Nueva extracción</Link>
        </div>

        {error && (
          <div className="card" style={{ borderLeft: '3px solid var(--danger)' }}>
            <div style={{ color: 'var(--danger)', fontWeight: 600, marginBottom: 4 }}>{error}</div>
            <button className="btn btn-ghost btn-sm" onClick={() => window.location.reload()}>Reintentar</button>
          </div>
        )}

        {loading && <LoadingCockpit />}

        {!loading && agg && quota && trend && (
          <Cockpit
            quota={quota}
            planName={planName}
            agg={agg}
            trend={trend}
            recentJobs={recentJobs}
            items={items}
          />
        )}
      </div>
    </div>
  )
}

function LoadingCockpit() {
  return (
    <div className="cockpit">
      {[3, 6, 3, 4, 3, 5, 12, 7, 5].map((span, i) => (
        <div key={i} className={`card col-${span}`} style={{ minHeight: span >= 12 ? 90 : 150 }}>
          <div className="skeleton-line" style={{ height: 12, width: '40%', marginBottom: 14 }} />
          <div className="skeleton-line" style={{ height: 8, width: '80%', marginBottom: 8 }} />
          <div className="skeleton-line" style={{ height: 8, width: '60%' }} />
        </div>
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------------------
 * Cockpit (datos)
 * ------------------------------------------------------------------------- */
function Cockpit({
  quota, planName, agg, trend, recentJobs, items,
}: {
  quota: AccountQuota
  planName: string
  agg: HomeAggregates
  trend: TrendAgg
  recentJobs: UserJobSummary[]
  items: InboxItem[]
}) {
  const days = daysUntil(quota.period_end_at)
  const maxCat = Math.max(1, ...agg.categories.map((c) => c.count))
  const maxTrend = Math.max(1, ...trend.buckets.map((b) => b.leads))
  const maxDist = Math.max(1, ...agg.rating.distribution)
  const totalFmt = nInt(agg.totalLeads)
  const [mapExpanded, setMapExpanded] = useState(false)

  return (
    <div className="cockpit">
      {/* CUPO */}
      <div className="card col-3 accent-edge">
        <div className="card-head">
          <span className="ic"><Icon name="clock" /></span>
          <div><h2>Cupo del plan</h2><div className="sub">{planName} · renueva en {days} d</div></div>
        </div>
        <div className="donut-wrap">
          <Donut consumed={quota.monthly_leads_consumed} quota={quota.monthly_leads_quota} />
          <div className="donut-meta">
            <div><div className="pl">Restante</div><div className="mval"><b className="mono">{nInt(quota.monthly_leads_remaining)}</b> leads</div></div>
            <div className="kv"><span>Usado este ciclo</span><b>{nInt(quota.monthly_leads_consumed)}</b></div>
            <div className="kv"><span>Días al reinicio</span><b>{days}</b></div>
          </div>
        </div>
      </div>

      {/* MAPA */}
      <div className="card col-6 map-card">
        <div className="card-head">
          <span className="ic"><Icon name="map" /></span>
          <div><h2>Mis zonas con leads</h2><div className="sub">{agg.zones.length} ciudades activas</div></div>
          <span className="right" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="mono">{totalFmt} leads</span>
            {agg.totalLeads > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMapExpanded(true)}>
                <Icon name="map" />Expandir
              </button>
            )}
          </span>
        </div>
        <MapZones items={items} height={200} />
        <MapExpandOverlay open={mapExpanded} items={items} onClose={() => setMapExpanded(false)} />
      </div>

      {/* CALIDAD */}
      <div className="card col-3">
        <div className="card-head">
          <span className="ic"><Icon name="check-circle" /></span>
          <div><h2>Calidad de la base</h2><div className="sub">{totalFmt} leads</div></div>
        </div>
        <div className="quality">
          <QualityRow icon="mail" label="Con email" value={agg.quality.email} />
          <QualityRow icon="globe" label="Con web" value={agg.quality.web} />
          <QualityRow icon="phone" label="Con teléfono" value={agg.quality.phone} />
          <QualityRow icon="star" label="Reclamados" value={agg.quality.claimed} warn />
        </div>
      </div>

      {/* CATEGORÍAS */}
      <div className="card col-4">
        <div className="card-head">
          <span className="ic"><Icon name="bars" /></span>
          <div><h2>Por categoría</h2><div className="sub">tu bandeja</div></div>
        </div>
        <div className="dist">
          {agg.categories.map((c) => (
            <div className="dist-row" key={c.label}>
              <span className="d-lbl">{c.label}</span>
              <div className="d-bar"><i style={{ width: `${(c.count / maxCat) * 100}%` }} /></div>
              <span className="d-n">{nInt(c.count)}</span>
            </div>
          ))}
          {agg.categories.length === 0 && <div className="empty-hint">Aún no hay categorías en tu bandeja.</div>}
        </div>
      </div>

      {/* VALORACIÓN */}
      <div className="card col-3">
        <div className="card-head">
          <span className="ic"><Icon name="star" /></span>
          <div><h2>Valoración media</h2><div className="sub">de tus leads</div></div>
        </div>
        <div className="rating-big"><span className="r-num">{nDec1(agg.rating.avg)}</span><span className="stars">★★★★★</span></div>
        <div className="rating-sub"><b>{nInt(agg.rating.totalReviews)}</b> reseñas agregadas · <b>{agg.rating.pctHigh}%</b> ≥ 4,5★</div>
        <div className="histo">
          {agg.rating.distribution.map((v, i) => (
            <div className={`b${i >= 3 ? ' hot' : ''}`} key={i} style={{ height: `${Math.max(6, (v / maxDist) * 100)}%` }}>
              <span>{i + 1}★</span>
            </div>
          ))}
        </div>
      </div>

      {/* TENDENCIA */}
      <div className="card col-5">
        <div className="card-head">
          <span className="ic"><Icon name="trend" /></span>
          <div><h2>Tendencia de extracciones</h2><div className="sub">últimas {trend.buckets.length} semanas</div></div>
        </div>
        <div className="trend">
          {trend.buckets.map((b, i) => (
            <div className={`col${i === trend.buckets.length - 1 ? ' last' : ''}`} key={b.label}>
              <div className="bar2" style={{ height: `${Math.max(3, (b.leads / maxTrend) * 100)}%` }} />
              <span className="t-lbl">{b.label}</span>
            </div>
          ))}
        </div>
        <div className="trend-foot"><span>Total extraído · {trend.buckets.length} sem.</span><b>{nInt(trend.totalLeads)} leads</b></div>
      </div>

      {/* OPORTUNIDADES DEL DÍA */}
      <div className="card col-12 accent-edge">
        <div className="card-head">
          <span className="ic"><Icon name="bolt" /></span>
          <div><h2>Oportunidades del día</h2><div className="sub">negocios con oportunidad alta · listos para contactar</div></div>
          <span className="right mono">{agg.opportunities.length} destacadas</span>
        </div>
        {agg.opportunities.length > 0 ? (
          <>
            <div className="oppday">
              {agg.opportunities.map((o) => (
                <div className="oppcard" key={o.placeId}>
                  <div className="oc-top">
                    <div><div className="oc-name">{o.title}</div><div className="oc-cat">{o.category} · {o.city}</div></div>
                    <span className={`oc-score ${o.band}`}><Sem band={o.band} /><b>{o.score}</b></span>
                  </div>
                  <div className="oc-sigs">
                    {o.signals.slice(0, 2).map((s) => <span className={`sig ${s.kind}`} key={s.label}>{s.label}</span>)}
                  </div>
                  <OppActions o={o} />
                </div>
              ))}
            </div>
            <div className="oppday-foot">
              <span>Detectadas con tus datos: reputación alta + carencia (sin web / no reclamado / pocas reseñas).</span>
              <Link to="/app/busqueda" className="btn btn-ghost btn-sm">Ver todas en Búsqueda →</Link>
            </div>
          </>
        ) : (
          <div className="empty-hint">Cuando extraigas leads, aquí aparecerán los de mayor oportunidad de venta.</div>
        )}
      </div>

      {/* CONTINÚA */}
      <div className="card col-7">
        <div className="card-head">
          <span className="ic"><Icon name="history" /></span>
          <div><h2>Continúa donde lo dejaste</h2><div className="sub">tus últimas búsquedas</div></div>
        </div>
        {recentJobs.length > 0 ? (
          <div className="recent">
            <div className="head"><span>Búsqueda</span><span>Estado</span><span>Leads</span><span>Cuándo</span><span /></div>
            {recentJobs.map((j) => {
              const st = jobStatusMeta(j.status)
              const zone = jobZoneLabel(j)
              const when =
                j.status === 'completed'
                  ? j.completed_at ?? j.created_at
                  : j.created_at
              return (
                <Link to="/app/busqueda" className="row" key={j.id}>
                  <span className="biz">
                    <span className="ico2"><Icon name="search" /></span>
                    <span>
                      <span className="name">{jobCategoryLabel(j)}</span>
                      {zone && <span className="zone"> · {zone}</span>}
                    </span>
                  </span>
                  <span><span className={`status ${st.cls}`}><span className="d" />{st.label}</span></span>
                  <span className="leads">{nInt(jobLeads(j))} <small>leads</small></span>
                  <span className="date">{formatDateTimeShort(when)}</span>
                  <span className="go"><Icon name="arrow" /></span>
                </Link>
              )
            })}
          </div>
        ) : (
          <div className="empty-hint">Todavía no tienes búsquedas. Lanza tu primera extracción.</div>
        )}
      </div>

      {/* INTELIGENCIA PREMIUM (teaser) */}
      <div className="premium col-5">
        <div className="ribbon"><span className="badge soon"><Icon name="lock" />Próximamente</span></div>
        <div className="card-head" style={{ marginBottom: 6 }}>
          <span className="ic"><Icon name="brain" /></span>
          <div><h2>Inteligencia premium por zona</h2><div className="sub">datos de inteligencia ampliada</div></div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--muted)', margin: '6px 0 10px' }}>
          Vista previa. Estos módulos enriquecen cada negocio y zona con señales de oportunidad digital — se activan en tu plan superior.
        </p>
        <div className="intel-list">
          <IntelRow icon="search" name="Visibilidad SEO estimada" desc="posición media · keywords visibles" skel />
          <IntelRow icon="trend" name="Tráfico web estimado" desc="visitas/mes · tendencia" skel />
          <IntelRow icon="screen" name="Tecnología y estado de la web" desc="CMS · velocidad · SSL · móvil" />
          <IntelRow icon="users" name="Competencia en la zona" desc="densidad · ranking relativo" />
        </div>
        <div className="premium-foot">
          <span>Incluye informe de auditoría exportable como gancho de venta.</span>
          <Link to="/app/plan" className="btn btn-ghost btn-sm">Ver plan superior</Link>
        </div>
      </div>
    </div>
  )
}

function QualityRow({ icon, label, value, warn }: { icon: IconName; label: string; value: number; warn?: boolean }) {
  return (
    <div className="q-row">
      <div className="q-top">
        <span className="q-lbl"><Icon name={icon} />{label}</span>
        <span className="q-val">{value}%</span>
      </div>
      <div className={`track${warn ? ' warn' : ''}`}><i style={{ width: `${value}%` }} /></div>
    </div>
  )
}

function IntelRow({ icon, name, desc, skel }: { icon: IconName; name: string; desc: string; skel?: boolean }) {
  return (
    <div className="intel">
      <span className="ii"><Icon name={icon} /></span>
      <div><div className="i-name">{name}</div><div className="i-desc">{desc}</div></div>
      {skel ? <span className="skel" /> : <span className="lock"><Icon name="lock" />Bloqueado</span>}
    </div>
  )
}
